
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { useState } from 'react';
import {
  UploadBox,
  UploadHelper,
  Button,
  Category,
  Icon,
  Margin,
  Size,
  AttachmentBox,
  AttachmentList,
  uploadFiles,
} from '@drawbotics/react-drylus';
import Playground from '~/components/Playground';
import { RenderChildren } from '../utils';
export const ExampleWrapper = ({
  children
}) => {
  const [error, setError] = useState();
  return children({
    error,
    setError
  });
};

const layoutProps = {
  ExampleWrapper
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


<h1>{`Upload Box`}</h1>
<p>{`This component is displayed to show a "upload file" cue to the user. It can be clicked, or used as a drag-and-drop area for files. The component also exposes the `}<inlineCode parentName="p">{`uploadFile`}</inlineCode>{` and `}<inlineCode parentName="p">{`uploadFiles`}</inlineCode>{` functions that can be used with any other element to trigger an upload.`}</p>
<p><strong parentName="p">{`NOTE`}</strong>{`: this component on its own will not upload the file. Instead you must provide it with a `}<inlineCode parentName="p">{`onUploadFiles`}</inlineCode>{` function which will take care of the upload. Drylus exposes two file upload functions, which require a `}<inlineCode parentName="p">{`signingUrl`}</inlineCode>{` which should point to the place where the upload should be made. If you have a different implementation, you can use your own and the function call will be triggered automatically on drop or when the file(s) are selected. Depending on the implementation you might still need to call a mutation to create the final attachment object in the database.`}</p>
<pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { UploadBox } from '@drawbotics/react-drylus';
`}</code></pre>
<p><strong parentName="p">{`Example`}</strong></p>
<Playground __position={0} __code={`<UploadBox
  onUploadFiles={(files) => {
    uploadFiles(
      files,
      process.env.EXAMPLE_API_HOST + '/rails/active_storage/direct_uploads',
      {
        onComplete: (uploads) => console.log('Upload completed for: ', uploads.map((upload) => upload.file.name).join(', ')),
        onProgress: (file, e) => console.log('Upload progress: ' + Math.round((e.loaded / e.total) * 100) + '% for', file.name),
      },
    )
  }}/>`} component={UploadBox} mdxType="Playground">
  <UploadBox onUploadFiles={files => {
        uploadFiles(files, process.env.EXAMPLE_API_HOST + '/rails/active_storage/direct_uploads', {
          onComplete: uploads => console.log('Upload completed for: ', uploads.map(upload => upload.file.name).join(', ')),
          onProgress: (file, e) => console.log('Upload progress: ' + Math.round(e.loaded / e.total * 100) + '% for', file.name)
        });
      }} mdxType="UploadBox" />
</Playground>
<p>{`The component is most often coupled with the `}<a parentName="p" {...{
        "href": "/component-kit/components/attachment-box"
      }}><inlineCode parentName="a">{`AttachmentBox`}</inlineCode></a>{` and `}<a parentName="p" {...{
        "href": "/component-kit/components/attachment-list"
      }}><inlineCode parentName="a">{`AttachmentList`}</inlineCode></a>{` component to display the upload progress.`}</p>
<p>{`Try to upload some files to see it in action. The example is limited to 1 file at a time.`}</p>
<Playground __position={1} __code={`<RenderChildren defaultValue={{}}>
  {({ state, setState }) => (
    <div style={{ width: 400 }}>
      <Margin size={{ bottom: Size.DEFAULT }}>
        <UploadBox
          onUploadFiles={(files) => {
            uploadFiles(
              files,
              process.env.EXAMPLE_API_HOST + '/rails/active_storage/direct_uploads',
              {
                onFinish: (file) => setState({ ...state, [file.name]: -1 }),
                onProgress: (file, e) => setState({ ...state, [file.name]: (e.loaded / e.total) }),
              },
            )
          }}
          fullWidth />
      </Margin>
      <AttachmentList>
        {Object.keys(state).map((filename) => (
          <AttachmentBox
            key={filename}
            onClickDownload={state[filename] === -1 ? () => {} : null}
            onClickClose={() => {}}
            fileName={filename} progress={state[filename] != -1 ? state[filename] : null} />
        ))}
      </AttachmentList>
    </div>
  )}
</RenderChildren>`} mdxType="Playground">
  <RenderChildren defaultValue={{}} mdxType="RenderChildren">
    {({
          state,
          setState
        }) => <div style={{
          width: 400
        }}>
        <Margin size={{
            bottom: Size.DEFAULT
          }} mdxType="Margin">
          <UploadBox onUploadFiles={files => {
              uploadFiles(files, process.env.EXAMPLE_API_HOST + '/rails/active_storage/direct_uploads', {
                onFinish: file => setState({
                  ...state,
                  [file.name]: -1
                }),
                onProgress: (file, e) => setState({
                  ...state,
                  [file.name]: e.loaded / e.total
                })
              });
            }} fullWidth mdxType="UploadBox" />
        </Margin>
        <AttachmentList mdxType="AttachmentList">
          {Object.keys(state).map(filename => <AttachmentBox key={filename} onClickDownload={state[filename] === -1 ? () => {} : null} onClickClose={() => {}} fileName={filename} progress={state[filename] != -1 ? state[filename] : null} mdxType="AttachmentBox" />)}
        </AttachmentList>
      </div>}
  </RenderChildren>
</Playground>
<h2>{`Error example`}</h2>
<p>{`By default the upload box does not allow for multiple files, so if someone tries to drag and drop more than 1 file nothing happens. You are provided a `}<inlineCode parentName="p">{`onMaxFilesExceeded`}</inlineCode>{` callback that can be used to set the component to its error state if you want to show a message; you can also show an alert for example.`}</p>

<Playground __position={2} __code={`<ExampleWrapper>
  {({ error, setError }) => (
    <UploadBox
      error={error}
      onMaxFilesExceeded={() => setError('You can only upload 1 file at a time')}
      onInit={() => setError(null)}
      onComplete={(uploads) => console.log('Upload completed for: ', uploads.map((upload) => upload.file.name).join(', '))}
      onProgress={(file, e) => console.log('Upload progress: ' + Math.round((e.loaded / e.total) * 100) + '% for', file.name)}
      signinUrl={process.env.EXAMPLE_API_HOST + '/rails/active_storage/direct_uploads'} />
  )}
</ExampleWrapper>`} mdxType="Playground">
  <ExampleWrapper mdxType="ExampleWrapper">
    {({
          error,
          setError
        }) => <UploadBox error={error} onMaxFilesExceeded={() => setError('You can only upload 1 file at a time')} onInit={() => setError(null)} onComplete={uploads => console.log('Upload completed for: ', uploads.map(upload => upload.file.name).join(', '))} onProgress={(file, e) => console.log('Upload progress: ' + Math.round(e.loaded / e.total * 100) + '% for', file.name)} signinUrl={process.env.EXAMPLE_API_HOST + '/rails/active_storage/direct_uploads'} mdxType="UploadBox" />}
  </ExampleWrapper>
</Playground>
<h2>{`Upload Helper`}</h2>
<p>{`Used internally by `}<inlineCode parentName="p">{`UploadBox`}</inlineCode>{`, this style-less component can be used to wrap any element that can be used as an upload trigger e.g. a button. Note that only "click" events will trigger the file browser, and not onDrop, like is the case with the `}<inlineCode parentName="p">{`UploadBox`}</inlineCode>{`. If you want `}<inlineCode parentName="p">{`onDrop`}</inlineCode>{` functionality for custom component you should implement it yourself.`}</p>
<pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { UploadHelper } from '@drawbotics/react-drylus';
`}</code></pre>
<Playground __position={3} __code={`<UploadHelper>
  <Button category={Category.BRAND} leading={<Icon name="upload" />}>Upload</Button>
</UploadHelper>`} component={UploadHelper} mdxType="Playground">
  <UploadHelper mdxType="UploadHelper">
    <Button category={Category.BRAND} leading={<Icon name="upload" mdxType="Icon" />} mdxType="Button">Upload</Button>
  </UploadHelper>
</Playground>
<h2>{`Exposed functions`}</h2>
<p>{`These are upload functions which use the `}<inlineCode parentName="p">{`signingUrl`}</inlineCode>{` and are exposed for convenience if this file upload method is enough for the use case.`}</p>
<pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { uploadFiles, uploadFile } from '@drawbotics/react-drylus';
`}</code></pre>
<p><inlineCode parentName="p">{`uploadFiles`}</inlineCode>{` takes 3 arguments:`}</p>
<ul>
<li parentName="ul"><inlineCode parentName="li">{`files`}</inlineCode>{`: the list of files to be uploaded, should be of `}<inlineCode parentName="li">{`FileList`}</inlineCode>{` type, retrieved from the input event or equivalent`}</li>
<li parentName="ul"><inlineCode parentName="li">{`signinUrl`}</inlineCode>{`: the URL where the attachments will be signed, usually ends with `}<inlineCode parentName="li">{`direct_upload`}</inlineCode></li>
<li parentName="ul"><inlineCode parentName="li">{`extra`}</inlineCode>{`: an object containing all the event callbacks (see `}<inlineCode parentName="li">{`UploadHelper`}</inlineCode>{` props' starting with `}<inlineCode parentName="li">{`on`}</inlineCode>{`), in addition to a `}<inlineCode parentName="li">{`sanitize`}</inlineCode>{` function, again same type as the `}<inlineCode parentName="li">{`UploadHelper`}</inlineCode>{` one.`}</li>
</ul>
<p><inlineCode parentName="p">{`uploadFile`}</inlineCode>{` is similar, also takes 3 arguments like above, but:`}</p>
<ul>
<li parentName="ul"><inlineCode parentName="li">{`file`}</inlineCode>{`: singular, file to be uploaded`}</li>
<li parentName="ul"><inlineCode parentName="li">{`siningUrl`}</inlineCode>{`: idem`}</li>
<li parentName="ul"><inlineCode parentName="li">{`extra`}</inlineCode>{`: same object as above but without the `}<inlineCode parentName="li">{`onInit`}</inlineCode>{` and `}<inlineCode parentName="li">{`onComplete`}</inlineCode>{` callbacks since those are called when all the multiple attachments are uploaded. For the same result with this function you may use the equivalent `}<inlineCode parentName="li">{`onStart`}</inlineCode>{` and `}<inlineCode parentName="li">{`onFinish`}</inlineCode></li>
</ul>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;